import { filename } from 'pathe/utils'
import { arrayByKey } from '~/utility/array'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import CATEGORIES from '../../../json/categories.json'
import { CampsiteCategoriesPresenter } from '../CampsiteCategoriesPresenter'

type CategoryJson = (typeof CATEGORIES)[0]

export class CampsiteCategoriesUpdaterJson {
  private presenter: CampsiteCategoriesPresenter = {
    list: [],
    byIdMap: {},
    bySlugMap: {},
    categoryImagesDataUrls: {},
    subcategoryFacetSlugs: [],
    categorySelectorFacetSlugs: [],
  }

  private language: string

  constructor(language: string) {
    this.language = language.replace('-', '_')
    this.generateData()
  }

  getPresenter() {
    return this.presenter
  }

  private generateData() {
    const list = CATEGORIES.map((category) =>
      this.map(this.language, category),
    ).sort((a, b) => {
      if (a.isByo && !b.isByo) {
        return -1
      }
      if (!a.isByo && b.isByo) {
        return 1
      }
      return a.order < b.order ? -1 : 1
    })
    this.presenter.list = list
    this.presenter.byIdMap = arrayByKey(list, 'id')
    this.presenter.bySlugMap = arrayByKey(list, 'slug')
    this.presenter.categoryImagesDataUrls = this.categoryImagesDataUrls

    // Collect BYO subcategory facet slugs (used for mutual exclusivity in facet selection)
    // These are: tent, motorhome, touring-caravan, etc.
    // Excludes: Onsite subcategory facets (bell-tents, yurts, cabins)
    const subcategoryFacetSlugs: string[] = []
    list.forEach((category) => {
      if (
        category.isByo &&
        category.subcategories &&
        category.subcategories.length > 0
      ) {
        category.subcategories.forEach((subcategory) => {
          if (subcategory.facetSlug) {
            subcategoryFacetSlugs.push(subcategory.facetSlug)
          }
        })
      }
    })
    this.presenter.subcategoryFacetSlugs = Array.from(
      new Set(subcategoryFacetSlugs),
    )

    // CategoriesSelector facets = subcategory facets + electric (for homepage preservation)
    this.presenter.categorySelectorFacetSlugs = [
      'electric',
      ...this.presenter.subcategoryFacetSlugs,
    ]
  }

  getCategoryImagesAsDataUrls() {
    const glob = import.meta.glob<Record<string, string>>(
      '~/assets/images/category-icons/*.svg',
      { query: '?raw&inline', eager: true },
    )
    const images = Object.fromEntries(
      Object.entries(glob).map(([key, value]) => [
        filename(key),
        `data:image/svg+xml;base64,${btoa(value.default)}`,
      ]),
    )
    return images
  }

  categoryImagesDataUrlsStore: { [k: string]: string } = {}
  get categoryImagesDataUrls() {
    if (Object.keys(this.categoryImagesDataUrlsStore).length === 0) {
      this.categoryImagesDataUrlsStore = this.getCategoryImagesAsDataUrls()
    }

    return this.categoryImagesDataUrlsStore
  }

  private map(language: string, category: CategoryJson): CampsiteCategory {
    const getImageDataUrl = () => {
      return this.categoryImagesDataUrls[category.slug]
    }

    const subcategories = category.subcategories?.map((sub) => ({
      id: sub.id.toString(),
      name: sub[`name_${language}`] || sub.name,
      slug: sub.slug,
      facetSlug: sub.facet_slug,
    }))

    return {
      id: category.id.toString(),
      rootSlug: category.root_slug,
      slug: category.slug,
      name: category[`name_${language}`] || category.name,
      pretty_name:
        category[`_pretty_name_${language}`] || category._pretty_name,
      breadcrumbName: category[`name_${language}`] || category.name,
      isByo: category.is_byo,
      order: category.order,
      getImageDataUrl,
      subcategories: subcategories || [],
    }
  }
}

export function getCampsiteCategoriesPresenter(language: string) {
  return new CampsiteCategoriesUpdaterJson(language).getPresenter()
}
